import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { 
  Users, 
  Mail, 
  Shield, 
  Lock, 
  Unlock, 
  Search, 
  RefreshCw, 
  RotateCw,
  ShieldCheck,
  ChevronRight, 
  BarChart3, 
  Clock,
  ArrowLeft,
  Settings as SettingsIcon,
  CircleDot,
  AlertCircle,
  FileText,
  Inbox,
  Send,
  History,
  MessageSquare,
  Plus,
  ArrowRight,
  ExternalLink,
  Check,
  Minus,
  Globe,
  Smartphone,
  Activity,
  Download,
  Bug,
  Rocket,
  Eye,
  Trash2,
  TrendingUp,
  Database
} from 'lucide-react';
import { AppUpdate } from '@/types';

interface EmailLog {
  id: string;
  recipient_email: string;
  subject: string;
  body_html: string;
  event_type: string;
  provider_message_id: string;
  sent_at: number;
  status: string;
}

interface InboundEmail {
  id: string;
  sender_email: string;
  sender_name: string;
  subject: string;
  body_text: string;
  received_at: number;
}

interface Subscriber {
  id: number;
  email: string;
  plan: string;
  created_at: string;
}

interface UserData {
  id: string;
  email: string;
  name: string;
  username?: string;
  picture?: string;
  plan: string;
  created_at: number;
  last_login_at?: number;
  is_suspended?: number;
  total_score?: number;
  total_play_time?: number;
  studio_time?: number;
  country_code?: string;
  device_info?: string;
  access_mode?: string;
  access_expires_at?: number | null;
  is_beta_tester?: number;
  trial_plan?: string;
  beta_expires_at?: number | null;
  beta_granted_at?: number | null;
  beta_granted_by?: string;
}

interface ModeStat {
  mode: string;
  time_spent: number;
}

interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  plan_id: string;
  paypal_order_id: string;
  created_at: number;
}

interface UILock {
  id: number;
  setting_id: string;
  min_plan: string;
  restricted_mode: string;
  is_active: number;
}

interface BugReport {
  id: number;
  user_id: string | null;
  email: string | null;
  title: string;
  description: string;
  status: string;
  priority: string;
  browser_info: string | null;
  resolution_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface SecurityEvent {
  id: string;
  user_id: string | null;
  user_email?: string | null;
  event_type: string;
  ip_address: string | null;
  details: string;
  created_at: string;
}

const LOCKABLE_SETTINGS = [
  // Top Level Tabs
  { id: 'audioTab', label: 'Audio Studio (Entire Tab)' },
  { id: 'visualsTab', label: 'Art Tab (Entire Tab)' },
  { id: 'recorderTab', label: 'Video Recorder (Entire Tab)' },
  { id: 'cloudTab', label: 'Cloud Saves (Entire Tab)' },
  
  // Audio Panel
  { id: 'audioMaster', label: 'Audio: Mixer & Volume Master' },
  { id: 'audioWorkshop', label: 'Audio: Synthesis Workshop' },
  { id: 'audioSamples', label: 'Audio: Samples & Processing' },
  { id: 'audioVolume', label: 'Audio: Mixer Segment' },
  { id: 'audioPostProcessing', label: 'Audio: Post Processing' },
  { id: 'melodyLibrary', label: 'Audio: Melody Library' },
  { id: 'midiFilter', label: 'Audio: MIDI Filter' },
  { id: 'customSounds', label: 'Audio: Custom Sound Upload' },
  { id: 'songConverter', label: 'Audio: Song Converter' },
  { id: 'rhythmicSyncOffset', label: 'Audio: Sync Offset' },
  { id: 'songSpeedIncreaseEnabled', label: 'Audio: Song Speed Increase' },

  // Visuals / Art Panel
  { id: 'sphereStudio', label: 'Visuals: Sphere Art Studio' },
  { id: 'sphereDisplay', label: 'Visuals: Sphere Visuals' },
  { id: 'generalDisplay', label: 'Visuals: Master Display' },
  { id: 'visualStyles', label: 'Visuals: Styles & Themes' },
  { id: 'visualPresets', label: 'Visuals: Preset Library' },
  { id: 'rgbEngine', label: 'Visuals: RGB Master Engine' },
  { id: 'geometry', label: 'Visuals: Geometry & Physics Shapes' },
  { id: 'shapeSettings', label: 'Visuals: Shape Control' },
  { id: 'trails', label: 'Visuals: Trail Dynamics Engine' },
  { id: 'impactFX', label: 'Visuals: Impact Effects' },
  { id: 'blastFXColor', label: 'Visuals: Blast & FX Color' },
  { id: 'parallax', label: 'Visuals: Parallax Engine' },
  { id: 'titleFooter', label: 'Visuals: Title & Branding' },
  { id: 'layerSorting', label: 'Visuals: Depth & Layering' },
  { id: 'globalVisuals', label: 'Visuals: Shared Global Visuals' },
  { id: 'ringPattern', label: 'Visuals: Ring Pattern Engine' },

  // Engine / Physics Panel
  { id: 'modeSelector', label: 'Engine: Mode Switching' },
  { id: 'spawner', label: 'Engine: Generation (Spawner)' },
  { id: 'rhythmicEngine', label: 'Engine: Pulse / Rhythmic Engine' },
  { id: 'rhythmicBreakThreshold', label: 'Rhythmic: Pulse Sensitivity' },
  { id: 'escape', label: 'Engine: Escape Mechanics' },
  { id: 'physics', label: 'Engine: Core Kinematics' },
  { id: 'spaceSpiral', label: 'Engine: Spacetime / Spiral' },
  { id: 'ballPlacement', label: 'Engine: Spawn Protocol (Placement)' },
  { id: 'boundaryColliders', label: 'Space: Boundary Colliders' },
  { id: 'outsideCollider', label: 'Space: Boundary Settings' },
  { id: 'hud', label: 'Space: HUD & Interface' },
  { id: 'camera', label: 'Space: Camera Control' },
  { id: 'background', label: 'Space: Backdrop Engine' },
  { id: 'optimization', label: 'Perf: Scaling & Optimization' },

  // Video Recorder
  { id: 'STUDIO_RECORDER', label: 'Video: Render & Record Engine' },
  
  // Storage & Cloud
  { id: 'cloud_storage', label: 'Storage: Cloud Infrastructure' },
  { id: 'cloudSync', label: 'Storage: Sync Engine' },
  { id: 'bulk_operations', label: 'Storage: Bulk Export/Import' },
];

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'analytics' | 'subscribers' | 'users' | 'settings' | 'mail-center' | 'bug-reports' | 'app-updates' | 'reconciliation' | 'email-templates' | 'security-logs' | 'admin-security' | 'showcase'>('analytics');
  const [data, setData] = useState<{ 
    subscribers: Subscriber[], 
    users: UserData[], 
    locks: UILock[],
    emailLogs: EmailLog[],
    inboundEmails: InboundEmail[],
    bugReports: BugReport[],
    updates: AppUpdate[],
    securityLogs: SecurityEvent[],
    showcaseGifs: any[],
    adminMfaStatus?: { enabled: boolean, enabledAt: string, backupCodesRemaining: number },
    subscribersCount?: number,
    usersCount?: number,
    subscriberAnalytics?: {
      domains: { gmail: number, others: number },
      plans: { plan: string, count: number }[],
      trends: { day: string, count: number }[]
    }
  }>({
    subscribers: [],
    users: [],
    locks: [],
    emailLogs: [],
    inboundEmails: [],
    bugReports: [],
    updates: [],
    securityLogs: [],
    showcaseGifs: [],
    adminMfaStatus: { enabled: false, enabledAt: '', backupCodesRemaining: 0 }
  });

  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [userPayments, setUserPayments] = useState<PaymentRecord[]>([]);
  const [userModeStats, setUserModeStats] = useState<ModeStat[]>([]);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  
  // Access Management State
  const [isUpdatingAccess, setIsUpdatingAccess] = useState(false);
  const [accessUpdateData, setAccessUpdateData] = useState({
    mode: 'normal',
    trialPlan: '',
    duration: '7', // days
    customExpiresAt: '',
    isBeta: false
  });

  useEffect(() => {
    if (selectedUser) {
      let duration = '7';
      let customExpiresAt = '';
      
      const expiresAt = selectedUser.access_mode === 'beta_tester' 
        ? selectedUser.beta_expires_at 
        : selectedUser.access_expires_at;

      if (expiresAt) {
        const remainingSeconds = expiresAt - Math.floor(Date.now() / 1000);
        const remainingDays = Math.ceil(remainingSeconds / (24 * 60 * 60));
        if ([3, 7, 30, 90].includes(remainingDays)) {
          duration = String(remainingDays);
        } else {
          duration = 'custom';
          const date = new Date(expiresAt * 1000);
          const tzoffset = date.getTimezoneOffset() * 60000;
          const localISOTime = (new Date(date.getTime() - tzoffset)).toISOString().slice(0, 16);
          customExpiresAt = localISOTime;
        }
      } else if (selectedUser.access_mode && selectedUser.access_mode !== 'normal') {
        duration = 'unlimited';
      }

      setAccessUpdateData({
        mode: selectedUser.access_mode || 'normal',
        trialPlan: selectedUser.trial_plan || '',
        duration,
        customExpiresAt,
        isBeta: selectedUser.access_mode === 'beta_tester' || selectedUser.is_beta_tester === 1
      });
    }
  }, [selectedUser?.id]);

  // Admin MFA Management State
  const [mfaActionLoading, setMfaActionLoading] = useState(false);
  const [mfaTotp, setMfaTotp] = useState('');
  const [mfaNewTotp, setMfaNewTotp] = useState('');
  const [rotationData, setRotationData] = useState<any>(null);
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);
  const [mfaError, setMfaError] = useState('');
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [mailView, setMailView] = useState<'outbound' | 'inbound' | 'compose'>('outbound');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // States for adding a new lock rule
  const [isAddingLock, setIsAddingLock] = useState(false);
  const [newLockSettingId, setNewLockSettingId] = useState(LOCKABLE_SETTINGS[0].id);
  const [newLockPlan, setNewLockPlan] = useState('PRO');
  const [newLockMode, setNewLockMode] = useState('ALL');
  
  // Mail states
  const [mailTo, setMailTo] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [mailFrom, setMailFrom] = useState('notifications');
  const [isBulk, setIsBulk] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pendingLocks, setPendingLocks] = useState<Record<string, string>>({});
  const [isSavingLocks, setIsSavingLocks] = useState(false);

  // App Updates states
  const [editingUpdate, setEditingUpdate] = useState<Partial<AppUpdate> | null>(null);
  const [isSavingUpdate, setIsSavingUpdate] = useState(false);

  const [newShowcaseUrl, setNewShowcaseUrl] = useState('');
  const [newShowcaseType, setNewShowcaseType] = useState<'YOUTUBE' | 'INSTAGRAM'>('YOUTUBE');
  const [isAddingShowcaseLink, setIsAddingShowcaseLink] = useState(false);

  const [isReconciling, setIsReconciling] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [reconcileResults, setReconcileResults] = useState<any>(null);

  // Pagination & Search States
  const [userPage, setUserPage] = useState(1);
  const [userLimit, setUserLimit] = useState(10);
  const [userTotal, setUserTotal] = useState(0);

  const [subscriberPage, setSubscriberPage] = useState(1);
  const [subscriberLimit, setSubscriberLimit] = useState(10);
  const [subscriberTotal, setSubscriberTotal] = useState(0);

  const [emailLogPage, setEmailLogPage] = useState(1);
  const [emailLogLimit, setEmailLogLimit] = useState(10);
  const [emailLogTotal, setEmailLogTotal] = useState(0);
  const [emailLogSearch, setEmailLogSearch] = useState('');

  const [securityLogPage, setSecurityLogPage] = useState(1);
  const [securityLogLimit, setSecurityLogLimit] = useState(10);
  const [securityLogTotal, setSecurityLogTotal] = useState(0);
  const [securityLogSearch, setSecurityLogSearch] = useState('');

  const [emailTemplates, setEmailTemplates] = useState<any[]>([]);
  const [previewEmail, setPreviewEmail] = useState<any>(null);

  const displayDomain = "funcirclegames.com";

  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      const response = await apiFetch('/api/admin/stats');
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) navigate('/admin/login');
        return;
      }
      const stats = await response.json();
      setData(prev => ({ ...prev, ...stats }));
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const query = new URLSearchParams({
        page: userPage.toString(),
        limit: userLimit.toString(),
        search: searchQuery
      });
      const response = await apiFetch(`/api/admin/users?${query}`);
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, users: result.users }));
        setUserTotal(result.total);
      }
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const fetchSubscribersList = async () => {
    try {
      const query = new URLSearchParams({
        page: subscriberPage.toString(),
        limit: subscriberLimit.toString(),
        search: searchQuery
      });
      const response = await apiFetch(`/api/admin/subscribers?${query}`);
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, subscribers: result.subscribers }));
        setSubscriberTotal(result.total);
      }
    } catch (error) {
      console.error('Subscribers fetch error:', error);
    }
  };

  const fetchEmailLogsList = async () => {
    try {
      const query = new URLSearchParams({
        page: emailLogPage.toString(),
        limit: emailLogLimit.toString(),
        search: emailLogSearch
      });
      const response = await apiFetch('/api/admin/emails/logs?' + query);
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, emailLogs: result.logs }));
        setEmailLogTotal(result.total);
      }
    } catch (error) {
      console.error('Email logs fetch error:', error);
    }
  };

  const fetchSecurityLogsList = async () => {
    try {
      const query = new URLSearchParams({
        page: securityLogPage.toString(),
        limit: securityLogLimit.toString(),
        search: securityLogSearch
      });
      const response = await apiFetch('/api/admin/security-logs?' + query);
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, securityLogs: result.logs }));
        setSecurityLogTotal(result.total);
      }
    } catch (error) {
      console.error('Security logs fetch error:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiFetch('/api/admin/emails/templates');
      if (response.ok) {
        const templates = await response.json();
        setEmailTemplates(templates);
      }
    } catch (error) {
      console.error('Templates fetch error:', error);
    }
  };

  const fetchAdminMfaStatus = async () => {
    try {
      const response = await apiFetch('/api/admin/mfa');
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, adminMfaStatus: result }));
      }
    } catch (e) {
      console.error('Failed to fetch admin MFA status', e);
    }
  };

  const handleMfaAction = async (action: string, payload: any = {}) => {
    setMfaActionLoading(true);
    setMfaError('');
    try {
      const response = await apiFetch('/api/admin/mfa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...payload })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Action failed');

      if (action === 'regenerate_backup_codes') {
        setNewBackupCodes(result.backup_codes);
        setMfaTotp('');
        fetchAdminMfaStatus();
      } else if (action === 'rotate_start') {
        setRotationData(result);
      } else if (action === 'rotate_confirm') {
        setNewBackupCodes(result.backup_codes);
        setRotationData(null);
        setMfaTotp('');
        setMfaNewTotp('');
        fetchAdminMfaStatus();
      }
    } catch (e: any) {
      setMfaError(e.message);
    } finally {
      setMfaActionLoading(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchUsers(),
        fetchSubscribersList(),
        fetchEmailLogsList(),
        fetchSecurityLogsList(),
        fetchTemplates(),
        fetchAdminMfaStatus(),
        (async () => {
          const res = await apiFetch('/api/showcase-gifs');
          if (res.ok) {
            const result = await res.json();
            setData(prev => ({ ...prev, showcaseGifs: result.gifs || [] }));
          }
        })()
      ]);
      
      // Staticish tables
      const inboundRes = await apiFetch('/api/admin/emails/inbound');
      if (inboundRes.ok) {
        const inboundEmails = await inboundRes.json();
        setData(prev => ({ ...prev, inboundEmails }));
      }
      
      const bugRes = await apiFetch('/api/admin/bug-reports');
      if (bugRes.ok) {
        const bugReports = await bugRes.json();
        setData(prev => ({ ...prev, bugReports }));
      }
      
      const updatesRes = await apiFetch('/api/admin/updates');
      if (updatesRes.ok) {
        const updates = await updatesRes.json();
        setData(prev => ({ ...prev, updates }));
      }
    } catch (error) {
      console.error('Failed to fetch admin data', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if ((!mailTo && !isBulk) || !mailSubject || !mailBody) return;
    setIsSending(true);
    try {
      const response = await apiFetch('/api/admin/emails/send', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipients: isBulk ? [] : mailTo.split(',').map(e => e.trim()),
          subject: mailSubject,
          html: mailBody,
          from: mailFrom,
          isBulk
        })
      });

      if (response.ok) {
        const dataRes = await response.json();
        const failures = dataRes.results?.filter((r: any) => !r.success) || [];
        
        if (failures.length > 0) {
          const errors = failures.slice(0, 3).map((f: any) => `${f.email}: ${f.error}`).join('\n');
          alert(`Warning: ${failures.length} email(s) failed to send.\n\nTop errors:\n${errors}`);
        } else {
          alert('All emails sent successfully!');
          setMailView('outbound');
          setMailTo('');
          setMailSubject('');
          setMailBody('');
          fetchData();
        }
      } else {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }
    } catch (error: any) {
      console.error('Failed to send email:', error);
      alert('Failed to send email: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!isLoading) fetchUsers();
  }, [userPage, userLimit]);

  useEffect(() => {
    if (!isLoading) fetchSubscribersList();
  }, [subscriberPage, subscriberLimit]);

  useEffect(() => {
    if (!isLoading) fetchEmailLogsList();
  }, [emailLogPage, emailLogLimit, emailLogSearch]);

  useEffect(() => {
    if (!isLoading) fetchSecurityLogsList();
  }, [securityLogPage, securityLogLimit, securityLogSearch]);

  const handleSaveLocks = async () => {
    const changesCount = Object.keys(pendingLocks).length;
    if (changesCount === 0) return;

    setIsSavingLocks(true);
    
    try {
      await Promise.all(Object.entries(pendingLocks).map(([settingId, minPlan]) => 
        apiFetch('/api/admin/ui-locks', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({ 
            setting_id: settingId, 
            min_plan: minPlan, 
            restricted_mode: 'ALL',
            is_active: 1
          })
        })
      ));
      
      setPendingLocks({});
      await fetchData();
    } catch (error) {
      console.error('Failed to save UI locks', error);
    } finally {
      setIsSavingLocks(false);
    }
  };

  const handleUpdatePlan = async (userId: string, newPlan: string) => {
    try {
      const response = await apiFetch(`/api/admin/users/${userId}/plan`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ plan: newPlan })
      });
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Failed to update plan', error);
    }
  };

  const fetchUserPayments = async (userId: string) => {
    setIsPaymentLoading(true);
    try {
      const response = await apiFetch(`/api/admin/users/${userId}/payments`);
      if (response.ok) {
        const result = await response.json();
        setUserPayments(result.history || []);
      }
      
      const statsRes = await apiFetch(`/api/admin/users/${userId}/stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setUserModeStats(statsData.modeStats || []);
        if (selectedUser && selectedUser.id === userId) {
            setSelectedUser({
                ...selectedUser,
                country_code: statsData.country_code,
                device_info: statsData.device_info,
                studio_time: statsData.studio_time,
                total_play_time: statsData.total_play_time
            });
        }
      }
    } catch (error) {
      console.error('Failed to fetch user extra data', error);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (deleteConfirmEmail !== selectedUser.email) {
      alert('Email confirmation failed. Please match the user email exactly.');
      return;
    }

    if (!window.confirm(`Are you absolutely sure you want to PERMANENTLY delete ${selectedUser.email}? This cannot be undone.`)) {
      return;
    }

    setIsDeletingUser(true);
    try {
      const response = await apiFetch(`/api/admin/users/${selectedUser.id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        alert('User deleted successfully.');
        setSelectedUser(null);
        setDeleteConfirmEmail('');
        fetchData();
      } else {
        let errorMsg = 'Unknown error';
        try {
          const err = await response.json();
          errorMsg = err.error || errorMsg;
        } catch (e) {
          errorMsg = await response.text() || response.statusText;
        }
        alert('Delete failed: ' + errorMsg);
      }
    } catch (error) {
      console.error('Delete error', error);
      alert('Delete failed: An unexpected error occurred.');
    } finally {
      setIsDeletingUser(false);
    }
  };
  
  const handleUpdateSuspension = async (userId: string, currentStatus: boolean) => {
    // ... logic
  };

  const handleUpdateAccess = async () => {
    if (!selectedUser) return;
    setIsUpdatingAccess(true);
    try {
      let expiresAt: number | null = null;
      if (accessUpdateData.mode !== 'normal') {
        if (accessUpdateData.duration === 'custom' && accessUpdateData.customExpiresAt) {
          expiresAt = Math.floor(new Date(accessUpdateData.customExpiresAt).getTime() / 1000);
        } else if (accessUpdateData.duration !== 'unlimited') {
          const days = parseInt(accessUpdateData.duration);
          expiresAt = Math.floor(Date.now() / 1000) + (days * 24 * 60 * 60);
        }
      }

      const response = await apiFetch(`/api/admin/users/${selectedUser.id}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_mode: accessUpdateData.mode,
          expires_at: expiresAt,
          trial_plan: accessUpdateData.trialPlan || null
        })
      });

      if (response.ok) {
        alert('Access updated successfully');
        // Refresh users and selected user
        await fetchUsers();
        const updatedUserResponse = await apiFetch(`/api/admin/users/${selectedUser.id}`);
        if (updatedUserResponse.ok) {
          setSelectedUser(await updatedUserResponse.json());
        }
      } else {
        const err = await response.json();
        alert('Failed to update access: ' + (err.error || 'Unknown error'));
      }
    } catch (error: any) {
      alert('Error updating access: ' + error.message);
    } finally {
      setIsUpdatingAccess(false);
    }
  };

  const handleToggleSuspension = async (userId: string, currentStatus: boolean) => {
    try {
      const response = await apiFetch(`/api/admin/users/${userId}/suspend`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ is_suspended: !currentStatus })
      });
      if (response.ok) {
        if (selectedUser && selectedUser.id === userId) {
            setSelectedUser({ ...selectedUser, is_suspended: !currentStatus ? 1 : 0 });
        }
        fetchData();
      }
    } catch (error) {
      console.error('Failed to toggle suspension', error);
    }
  };

  const handleUpdateBugReport = async (id: number, status: string, notes: string) => {
    try {
      const response = await apiFetch('/api/admin/bug-reports', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ id, status, resolution_notes: notes })
      });
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Failed to update bug report', error);
    }
  };

  const handleSaveUpdate = async () => {
    if (!editingUpdate?.title || !editingUpdate?.content) {
      alert('Title and content are required.');
      return;
    }
    setIsSavingUpdate(true);
    try {
      const response = await apiFetch('/api/admin/updates', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(editingUpdate)
      });
      if (response.ok) {
        setEditingUpdate(null);
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to save update: ${errorData.details || errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to save update', error);
      alert('Error saving update');
    } finally {
      setIsSavingUpdate(false);
    }
  };

  const handleDeleteUpdate = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;
    try {
      const response = await apiFetch(`/api/admin/updates?id=${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchData();
      } else {
        alert('Failed to delete update');
      }
    } catch (error) {
      console.error('Failed to delete update', error);
      alert('Error deleting update');
    }
  };

  const handleAddLock = async () => {
    try {
      await apiFetch('/api/admin/ui-locks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            setting_id: newLockSettingId, 
            is_active: 1, 
            min_plan: newLockPlan, 
            restricted_mode: newLockMode 
        })
      });
      setIsAddingLock(false);
      fetchData();
    } catch (error) {
      console.error('Failed to add lock rule');
    }
  };

  const handleShowcaseUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiFetch('/api/admin/showcase-gifs', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        alert('Showcase file uploaded successfully');
        fetchData();
      } else {
        const error = await res.json();
        alert('Upload failed: ' + (error.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Upload failed');
    }
  };

  const handleShowcaseAddLink = async () => {
    if (!newShowcaseUrl) return;
    setIsAddingShowcaseLink(true);
    try {
      const res = await apiFetch('/api/admin/showcase-gifs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: newShowcaseType,
          url: newShowcaseUrl
        })
      });
      if (res.ok) {
        setNewShowcaseUrl('');
        alert('Link added successfully');
        fetchData();
      } else {
        const error = await res.json();
        const detailMsg = error.details ? `\n\nDetails: ${error.details}` : '';
        alert(`Failed to add link: ${error.error || 'Unknown error'}${detailMsg}`);
      }
    } catch (err: any) {
      console.error('Link addition error:', err);
      alert(`Operation failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsAddingShowcaseLink(false);
    }
  };

  const handleShowcaseDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this showcase item?')) return;
    try {
      const res = await apiFetch(`/api/admin/showcase-gifs?id=${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchData();
      } else {
        alert('Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleRunReconciliation = async () => {
    setIsReconciling(true);
    setReconcileResults(null);
    try {
      const response = await apiFetch('/api/admin/reconcile-subscriptions', {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        setReconcileResults(result.results);
        fetchData();
      } else {
        const err = await response.json();
        alert('Reconciliation failed: ' + (err.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Reconciliation error', error);
      alert('Reconciliation failed.');
    } finally {
      setIsReconciling(false);
    }
  };

  const handleMigrateDatabase = async () => {
    if (!window.confirm('This will ensure all database tables are initialized and exist. This is safe to run multiple times. Continue?')) return;
    setIsMigrating(true);
    try {
      const response = await apiFetch('/api/admin/migrate', {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        alert(`Migration successful!\n\nTables Checked: ${result.tablesChecked}\nNewly Created: ${result.newlyCreated}`);
      } else {
        const err = await response.json();
        alert('Migration failed: ' + (err.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Migration error', error);
      alert('Migration failed.');
    } finally {
      setIsMigrating(false);
    }
  };

  const filteredSubscribers = data.subscribers.filter(s => s.email.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredUsers = data.users.filter(u => u.email.toLowerCase().includes(searchQuery.toLowerCase()) || u.name?.toLowerCase().includes(searchQuery.toLowerCase()));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-50/50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-black animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-zinc-50/50 text-zinc-900 font-sans selection:bg-black/5 italic-none">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-zinc-950/10 z-50 flex flex-col shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-zinc-50 rounded-xl flex items-center justify-center border border-zinc-950/10">
              <Shield className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg tracking-tight text-zinc-950">Admin</h2>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Control Panel</p>
            </div>
          </div>

          <nav className="space-y-1">
            <NavItem 
              icon={<BarChart3 />} 
              label="Overview" 
              active={activeTab === 'analytics'} 
              onClick={() => setActiveTab('analytics')} 
            />
            <NavItem 
              icon={<Mail />} 
              label="Subscribers" 
              active={activeTab === 'subscribers'} 
              onClick={() => setActiveTab('subscribers')} 
            />
            <NavItem 
              icon={<Users />} 
              label="Users" 
              active={activeTab === 'users'} 
              onClick={() => setActiveTab('users')} 
            />
            <NavItem 
              icon={<SettingsIcon />} 
              label="Feature Locks" 
              active={activeTab === 'settings'} 
              onClick={() => setActiveTab('settings')} 
            />
            <NavItem 
              icon={<Inbox />} 
              label="Mail Center" 
              active={activeTab === 'mail-center'} 
              onClick={() => setActiveTab('mail-center')} 
            />
            <NavItem 
              icon={<Bug />} 
              label="Bug Reports" 
              active={activeTab === 'bug-reports'} 
              onClick={() => setActiveTab('bug-reports')} 
            />
            <NavItem 
              icon={<Rocket />} 
              label="App Updates" 
              active={activeTab === 'app-updates'} 
              onClick={() => setActiveTab('app-updates')} 
            />
            <NavItem 
              icon={<FileText />} 
              label="Mail Templates" 
              active={activeTab === 'email-templates'} 
              onClick={() => setActiveTab('email-templates')} 
            />
            <NavItem 
              icon={<Shield />} 
              label="Audit Logs" 
              active={activeTab === 'security-logs'} 
              onClick={() => setActiveTab('security-logs')} 
            />
            <NavItem 
              icon={<Lock />} 
              label="Admin Security" 
              active={activeTab === 'admin-security'} 
              onClick={() => setActiveTab('admin-security')} 
            />
            <NavItem 
              icon={<RefreshCw />} 
              label="Reconciliation" 
              active={activeTab === 'reconciliation'} 
              onClick={() => setActiveTab('reconciliation')} 
            />
            <NavItem 
              icon={<Eye />} 
              label="Showcase" 
              active={activeTab === 'showcase'} 
              onClick={() => setActiveTab('showcase')} 
            />
          </nav>
        </div>

        <div className="mt-auto p-6 border-t border-zinc-100">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-3 text-zinc-400 hover:text-zinc-900 transition-all text-xs font-bold group uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Site
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-10 max-w-[1600px] bg-zinc-50/50">
        <header className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-3xl font-display font-bold tracking-tight mb-1 text-zinc-950 uppercase">{activeTab === 'settings' ? 'Feature Locks' : activeTab.replace('-', ' ')}</h1>
            <p className="text-zinc-500 text-sm font-medium italic">Manage your community and system configuration.</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-black transition-colors" />
              <input 
                type="text" 
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-zinc-950/10 rounded-xl py-2.5 pl-12 pr-6 text-sm focus:border-black outline-none w-72 transition-all shadow-sm"
              />
            </div>
            <button 
              onClick={fetchData}
              className="p-2.5 bg-white border border-zinc-950/10 rounded-xl hover:border-black transition-all shadow-sm active:scale-95"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </header>

        {activeTab === 'analytics' && (
          <div className="space-y-8 animate-in mt-2 grayscale hover:grayscale-0 transition-all duration-500">
            <div className="grid grid-cols-3 gap-6">
              <StatCard label="Subscribers" value={data.subscribersCount || data.subscribers.length} icon={<Mail />} color="cyan" />
              <StatCard label="Registered Users" value={data.usersCount || data.users.length} icon={<Users />} color="purple" />
              <StatCard label="Protected Features" value={data.locks.filter(l => l.min_plan !== 'FREE').length} icon={<Lock />} color="pink" />
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
                <h3 className="text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-3 text-zinc-400">
                  <Clock className="w-5 h-5 text-zinc-300" />
                  Recent Signups
                </h3>
                <div className="space-y-3">
                  {data.subscribers.slice(0, 5).map(s => (
                    <div key={s.id} className="flex items-center justify-between p-4 bg-zinc-50 rounded-xl border border-zinc-100 group hover:border-cyan-200 transition-all">
                      <div className="flex flex-col">
                        <span className="font-bold text-sm text-zinc-900">{s.email}</span>
                        <span className="text-[10px] text-zinc-500 font-bold uppercase mt-0.5">Plan: {s.plan}</span>
                      </div>
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
                <h3 className="text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-3 text-zinc-400">
                  <CircleDot className="w-5 h-5 text-zinc-300" />
                  System Status
                </h3>
                <div className="space-y-6">
                  <HealthBar label="Database" status="Active" percentage={100} color="cyan" />
                  <HealthBar label="Storage" status="Optimal" percentage={92} color="purple" />
                  <HealthBar label="Latency" status="24ms" percentage={98} color="pink" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'subscribers' && (
          <div className="space-y-8 animate-in">
            {/* Analytics Dashboard */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Domain Stats */}
              <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-4 flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />
                  Email Domains
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Gmail', value: data.subscriberAnalytics?.domains.gmail || 0 },
                          { name: 'Other', value: data.subscriberAnalytics?.domains.others || 0 }
                        ]}
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        <Cell fill="#06b6d4" />
                        <Cell fill="#09090b" />
                      </Pie>
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
                    <span className="text-[10px] font-bold text-zinc-500 uppercase">Gmail: {data.subscriberAnalytics?.domains.gmail || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-zinc-950" />
                    <span className="text-[10px] font-bold text-zinc-500 uppercase">Others: {data.subscriberAnalytics?.domains.others || 0}</span>
                  </div>
                </div>
              </div>

              {/* Plan Stats */}
              <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-4 flex items-center gap-2">
                   <Rocket className="w-3.5 h-3.5" />
                   Plan Breakdown
                </h3>
                <div className="space-y-4 pt-2">
                  {data.subscriberAnalytics?.plans.map((p, idx) => {
                    const colors = ['#06b6d4', '#a855f7', '#ec4899', '#09090b'];
                    return (
                      <div key={p.plan} className="relative">
                        <div className="flex items-center justify-between mb-1.5 px-1">
                          <span className="text-[10px] font-bold text-zinc-900 uppercase tracking-tight">{p.plan}</span>
                          <span className="text-[10px] font-bold text-zinc-400">{p.count}</span>
                        </div>
                        <div className="h-1.5 w-full bg-zinc-50 rounded-full overflow-hidden border border-zinc-100">
                          <div 
                            className="h-full transition-all duration-1000" 
                            style={{ 
                              width: `${(p.count / Math.max(...data.subscriberAnalytics!.plans.map(pl => pl.count)) * 100)}%`,
                              backgroundColor: colors[idx % colors.length]
                            }} 
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Trend Graph */}
              <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-4 flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" />
                  Subscription Trend (30d)
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.subscriberAnalytics?.trends || []}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f4f4f5" />
                      <XAxis 
                        dataKey="day" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{fontSize: 9, fontWeight: 700, fill: '#a1a1aa'}}
                        tickFormatter={(val) => val.split('-').slice(1).join('/')}
                      />
                      <YAxis axisLine={false} tickLine={false} tick={{fontSize: 9, fontWeight: 700, fill: '#a1a1aa'}} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}
                      />
                      <Area type="monotone" dataKey="count" stroke="#06b6d4" strokeWidth={2} fillOpacity={1} fill="url(#colorCount)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="bg-white border border-zinc-950/10 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest">Email Address</th>
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest">Plan Tier</th>
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest text-center">Subscription Date</th>
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredSubscribers.map(s => (
                    <tr key={s.id} className="hover:bg-zinc-50/50 transition-all group">
                      <td className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-400">@</div>
                          <span className="font-bold text-sm text-zinc-900">{s.email}</span>
                        </div>
                      </td>
                      <td className="p-6">
                        <span className={getPlanStyle(s.plan)}>
                          {s.plan}
                        </span>
                      </td>
                      <td className="p-6 text-xs font-bold text-zinc-500 text-center uppercase tracking-widest">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td className="p-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Active</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredSubscribers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-20 text-center text-zinc-400 italic text-sm font-bold uppercase tracking-widest">No subscribers found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="p-6 border-t border-zinc-100 bg-zinc-50/30 flex items-center justify-between">
                <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                  Showing {data.subscribers.length} of {subscriberTotal} results
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    disabled={subscriberPage === 1}
                    onClick={() => setSubscriberPage(p => p - 1)}
                    className="px-4 py-2 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 hover:border-black transition-all"
                  >
                    Previous
                  </button>
                  <button 
                    disabled={data.subscribers.length < subscriberLimit || (subscriberPage * subscriberLimit >= subscriberTotal)}
                    onClick={() => setSubscriberPage(p => p + 1)}
                    className="px-4 py-2 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 hover:border-black transition-all"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
        </div>
      )}

        {activeTab === 'users' && (
          <div className="bg-white border border-zinc-950/10 rounded-2xl overflow-hidden shadow-sm animate-in">
            <div className="p-4 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Page Size</label>
                <select 
                  value={userLimit}
                  onChange={(e) => {
                    setUserLimit(parseInt(e.target.value));
                    setUserPage(1);
                  }}
                  className="bg-white border border-zinc-200 rounded-lg text-[10px] font-bold px-3 py-1.5 outline-none focus:border-black uppercase tracking-widest cursor-pointer"
                >
                  <option value={10}>10 Users</option>
                  <option value={50}>50 Users</option>
                  <option value={100}>100 Users</option>
                </select>
              </div>
              <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                Total Users: {userTotal}
              </div>
            </div>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest">User Details</th>
            <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest">Plan Level</th>
            <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest text-center">Stats</th>
            <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest">Registration</th>
            <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {filteredUsers.map(u => (
            <tr key={u.id} className="hover:bg-zinc-50/50 transition-all group">
              <td className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center overflow-hidden">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span 
                      onClick={() => {
                        setSelectedUser(u);
                        fetchUserPayments(u.id);
                      }}
                      className="font-bold text-sm text-zinc-900 group-hover:text-cyan-600 transition-colors uppercase tracking-tight cursor-pointer flex items-center gap-2"
                    >
                      {u.name || u.username || 'ANONYMOUS USER'}
                      {u.access_mode === 'beta_tester' && (
                        <span className="px-1.5 py-0.5 bg-purple-50 border border-purple-100 rounded text-[9px] font-bold text-purple-600 tracking-wide uppercase">BETA</span>
                      )}
                      {(u.access_mode === 'trial_creator_pro' || u.access_mode === 'trial_studio_elite') && (
                        <span className="px-1.5 py-0.5 bg-amber-50 border border-amber-100 rounded text-[9px] font-bold text-amber-600 tracking-wide uppercase">TRIAL</span>
                      )}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all" />
                    </span>
                    <span className="text-[10px] font-bold text-zinc-400 mt-1 uppercase tracking-widest">{u.email}</span>
                  </div>
                </div>
              </td>
              <td className="p-6">
                <select 
                  value={u.plan} 
                  onChange={(e) => handleUpdatePlan(u.id, e.target.value)}
                  className="bg-zinc-50 border border-zinc-200 rounded-lg text-[10px] font-bold px-3 py-1.5 outline-none focus:border-cyan-500/50 hover:bg-zinc-100 transition-all cursor-pointer appearance-none text-cyan-600 uppercase tracking-widest"
                >
                  <option value="FREE">FREE</option>
                  <option value="PRO">PRO</option>
                  <option value="ELITE">ELITE</option>
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </select>
              </td>
              <td className="p-6">
                <div className="flex flex-col items-center gap-1">
                   <div className="flex items-center gap-3 text-[9px] font-bold text-zinc-400 uppercase tracking-widest">
                     <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {u.total_score || 0}</span>
                     <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.round((u.total_play_time || 0) / 60)}m</span>
                   </div>
                   {u.last_login_at && (
                     <span className="text-[8px] text-zinc-300 uppercase tracking-tighter">Last Login: {new Date(u.last_login_at).toLocaleDateString()}</span>
                   )}
                </div>
              </td>
              <td className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest">
                <div className="text-right">
                  <div>{new Date(u.created_at).toLocaleDateString()}</div>
                  <div className="text-[8px] text-zinc-300 mt-0.5">{new Date(u.created_at * 1000).toLocaleTimeString()}</div>
                </div>
              </td>
              <td className="p-6 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button 
                    onClick={() => {
                      setSelectedUser(u);
                      fetchUserPayments(u.id);
                    }}
                    className="p-2 text-zinc-400 hover:text-black hover:bg-zinc-100 rounded-lg transition-all"
                    title="View Details"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => {
                        handleToggleSuspension(u.id, !!u.is_suspended);
                    }}
                    className={`p-2 rounded-lg transition-all ${u.is_suspended ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-zinc-300 hover:text-amber-500 hover:bg-amber-50'}`}
                    title={u.is_suspended ? "Unsuspend User" : "Suspend User"}
                  >
                    {u.is_suspended ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  </button>
                  <button 
                    onClick={() => {
                      setSelectedUser(u);
                      setDeleteConfirmEmail('');
                    }}
                    className="p-2 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    title="Delete User"
                  >
                    <AlertCircle className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-20 text-center text-zinc-400 italic text-sm font-bold uppercase tracking-widest">No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="p-6 border-t border-zinc-100 bg-zinc-50/30 flex items-center justify-between">
              <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                Showing page {userPage} of {Math.ceil(userTotal / userLimit)}
              </div>
              <div className="flex items-center gap-2">
                <button 
                  disabled={userPage === 1}
                  onClick={() => setUserPage(p => p - 1)}
                  className="px-4 py-2 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 hover:border-black transition-all"
                >
                  Previous
                </button>
                <button 
                  disabled={data.users.length < userLimit || (userPage * userLimit >= userTotal)}
                  onClick={() => setUserPage(p => p + 1)}
                  className="px-4 py-2 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 hover:border-black transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-8 animate-in">
            <div className="flex items-center justify-between">
              <div className="bg-white border border-zinc-950/10 rounded-2xl p-8 text-black flex-1 relative overflow-hidden group shadow-sm">
                <h3 className="font-bold text-xl flex items-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-black" />
                  Feature Access Controls
                </h3>
                <p className="text-sm text-zinc-500 leading-relaxed max-w-3xl font-medium">
                  Set the minimum plan level required to access specific features. Changes are staged locally and must be saved to apply.
                </p>
              </div>

              {Object.keys(pendingLocks).length > 0 && (
                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-4 bg-white border border-zinc-950 rounded-2xl p-4 animate-in fade-in slide-in-from-bottom-5 duration-300 shadow-2xl">
                  <div className="px-4">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Pending Updates</span>
                    <span className="text-sm font-bold text-zinc-900">{Object.keys(pendingLocks).length} features modified</span>
                  </div>
                  <div className="w-px h-10 bg-zinc-100" />
                  <button 
                    onClick={() => setPendingLocks({})}
                    className="px-6 py-2.5 text-[10px] font-bold text-zinc-400 hover:text-zinc-600 uppercase tracking-widest transition-all"
                  >
                    Discard
                  </button>
                  <button 
                    onClick={handleSaveLocks}
                    disabled={isSavingLocks}
                    className="px-8 py-2.5 bg-zinc-950 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest flex items-center gap-3 hover:bg-zinc-800 transition-all disabled:opacity-50 active:scale-95 shadow-lg shadow-zinc-950/20"
                  >
                    {isSavingLocks ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="p-8 text-xs font-bold text-zinc-500 uppercase tracking-widest w-1/3">Feature Name</th>
                       {['FREE', 'PRO', 'ELITE', 'ENTERPRISE'].map(plan => (
                        <th 
                          key={plan}
                          onClick={() => {
                            if (!window.confirm(`Stage all features to ${plan}?`)) return;
                            const newPending: Record<string, string> = {};
                            LOCKABLE_SETTINGS.forEach(s => {
                              const currentDbPlan = data.locks.find(l => l.setting_id === s.id && l.restricted_mode === 'ALL')?.min_plan || 'FREE';
                              if (plan !== currentDbPlan) {
                                newPending[s.id] = plan;
                              }
                            });
                            setPendingLocks(newPending);
                          }}
                          className={`p-8 text-center text-[10px] font-bold uppercase tracking-widest cursor-pointer hover:bg-zinc-100 transition-all relative group ${
                            plan === 'PRO' ? 'text-cyan-600' : plan === 'ELITE' ? 'text-purple-600' : plan === 'ENTERPRISE' ? 'text-amber-600' : 'text-zinc-500'
                          }`}
                        >
                          {plan}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                     {LOCKABLE_SETTINGS.map(setting => {
                      const currentLock = data.locks.find(l => l.setting_id === setting.id && l.restricted_mode === 'ALL');
                      const activeMinPlan = pendingLocks[setting.id] || currentLock?.min_plan || 'FREE';
                      const planHierarchy: Record<string, number> = { 'FREE': 0, 'PRO': 1, 'ELITE': 2, 'ENTERPRISE': 3 };
                      const minLevel = planHierarchy[activeMinPlan];
                      const isDirty = !!pendingLocks[setting.id];

                      return (
                        <tr key={setting.id} className={`hover:bg-zinc-50/30 group transition-all ${isDirty ? 'bg-cyan-50/20' : ''}`}>
                          <td className="p-8">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-zinc-900 group-hover:text-cyan-600 transition-colors uppercase tracking-tight">
                                  {setting.label}
                                </span>
                                {isDirty && <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />}
                              </div>
                              <span className="text-[10px] text-zinc-400 mt-1 uppercase tracking-widest font-bold">{setting.id}</span>
                            </div>
                          </td>
                          {['FREE', 'PRO', 'ELITE', 'ENTERPRISE'].map(plan => {
                            const planLevel = planHierarchy[plan];
                            const isPlanUnlocked = planLevel >= minLevel;
                            
                            return (
                              <td key={plan} className="p-4 text-center">
                                <button
                                  onClick={() => {
                                    const currentDbPlan = data.locks.find(l => l.setting_id === setting.id && l.restricted_mode === 'ALL')?.min_plan || 'FREE';
                                    if (plan === currentDbPlan) {
                                      setPendingLocks(prev => {
                                        const next = { ...prev };
                                        delete next[setting.id];
                                        return next;
                                      });
                                    } else {
                                      setPendingLocks(prev => ({ ...prev, [setting.id]: plan }));
                                    }
                                  }}
                                  className={`
                                    w-full py-4 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all flex flex-col items-center justify-center gap-2 border
                                    ${activeMinPlan === plan 
                                      ? getPlanStyle(plan) 
                                      : isPlanUnlocked 
                                        ? 'bg-zinc-50 text-zinc-400 border-zinc-100 opacity-60 hover:opacity-100 hover:bg-zinc-100' 
                                        : 'bg-white text-zinc-300 border-transparent opacity-30 hover:opacity-100 hover:border-zinc-200'
                                    }
                                    ${isDirty && activeMinPlan === plan ? 'ring-2 ring-cyan-500/20 ring-offset-2' : ''}
                                  `}
                                >
                                  {isPlanUnlocked ? (
                                    <Check className={`w-3.5 h-3.5 ${activeMinPlan === plan ? 'text-current' : 'opacity-40'}`} />
                                  ) : (
                                    <Minus className="w-3.5 h-3.5 opacity-20" />
                                  )}
                                  <span className="text-[8px] tracking-widest">{plan}</span>
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'mail-center' && (
          <div className="space-y-8 animate-in">
            <div className="flex gap-8">
              {/* Sidebar Shortcuts */}
              <div className="w-72 space-y-6">
                <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden p-2 shadow-sm">
                  <MailTabButton icon={<History />} label="Sent Messages" active={mailView === 'outbound'} onClick={() => setMailView('outbound')} count={data.emailLogs.length} />
                  <MailTabButton icon={<Inbox />} label="Incoming" active={mailView === 'inbound'} onClick={() => setMailView('inbound')} count={data.inboundEmails.length} />
                  <div className="h-px bg-zinc-100 my-2 mx-4" />
                  <MailTabButton icon={<Send />} label="Compose" active={mailView === 'compose' && !isBulk} onClick={() => { setMailView('compose'); setIsBulk(false); }} />
                  <MailTabButton icon={<Users />} label="Broadcast" active={mailView === 'compose' && isBulk} onClick={() => { setMailView('compose'); setIsBulk(true); }} />
                </div>

                <div className="bg-purple-50 border border-purple-100 rounded-2xl p-6 shadow-sm relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100/30 blur-2xl transition-all pointer-events-none" />
                  <div className="flex items-center gap-2 text-purple-600 text-xs font-bold uppercase tracking-widest mb-3">
                    <Shield className="w-4 h-4" />
                    Secure Service
                  </div>
                  <p className="text-[11px] text-zinc-500 leading-relaxed font-semibold">
                    All communications are routed through our global delivery network.
                  </p>
                </div>
              </div>

              {/* View Output */}
              <div className="flex-1 min-h-[600px]">
                {mailView === 'compose' ? (
                  <div className="bg-white border border-zinc-200 rounded-2xl p-8 space-y-8 shadow-sm">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold tracking-tight flex items-center gap-3 text-zinc-950 uppercase">
                        <MessageSquare className="text-cyan-600 w-6 h-6" />
                        {isBulk ? 'New Broadcast Campaign' : 'Compose Message'}
                      </h2>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">From Address</label>
                          <select 
                            value={mailFrom}
                            onChange={(e) => setMailFrom(e.target.value)}
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-sm outline-none focus:border-cyan-500/50 transition-all font-bold text-zinc-900 appearance-none cursor-pointer uppercase tracking-tight"
                          >
                            <option value="notifications">notifications@{displayDomain}</option>
                            <option value="support">support@{displayDomain}</option>
                            <option value="contact">contact@{displayDomain}</option>
                            <option value="legal">legal@{displayDomain}</option>
                            <option value="billing">billing@{displayDomain}</option>
                            <option value="marketing">marketing@{displayDomain}</option>
                          </select>
                        </div>
                        {!isBulk && (
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Recipient Email</label>
                            <input 
                              value={mailTo}
                              onChange={(e) => setMailTo(e.target.value)}
                              placeholder="user@email.com"
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-sm outline-none focus:border-cyan-500/50 transition-all text-zinc-900 placeholder:text-zinc-300 font-bold"
                            />
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Subject Line</label>
                        <input 
                          value={mailSubject}
                          onChange={(e) => setMailSubject(e.target.value)}
                          placeholder="What is this about?"
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-sm outline-none focus:border-cyan-500/50 transition-all text-zinc-900 placeholder:text-zinc-300 font-bold"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Message Body</label>
                        <textarea 
                          value={mailBody}
                          onChange={(e) => setMailBody(e.target.value)}
                          placeholder="Tell them something important..."
                          rows={10}
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-6 text-sm outline-none focus:border-cyan-500/30 font-sans resize-none transition-all text-zinc-700 placeholder:text-zinc-300 font-medium"
                        />
                      </div>

                      <button 
                        disabled={isSending}
                        onClick={handleSendEmail}
                        className="w-full bg-zinc-950 text-white font-bold py-4 rounded-xl hover:bg-zinc-900 active:scale-[0.98] disabled:opacity-50 transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-3 mt-4 shadow-xl shadow-zinc-950/10"
                      >
                        {isSending ? <RefreshCw className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4" />}
                        {isBulk ? `Broadcast to all users (${data.users.length})` : 'Send Message'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-4 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="relative flex-1 max-w-md">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                          <input 
                            type="text"
                            placeholder="Search emails or subjects..."
                            value={emailLogSearch}
                            onChange={(e) => {
                              setEmailLogSearch(e.target.value);
                              setEmailLogPage(1);
                            }}
                            className="bg-white border border-zinc-200 rounded-lg py-1.5 pl-10 pr-4 text-xs focus:border-black outline-none w-full transition-all"
                          />
                        </div>
                        <select 
                          value={emailLogLimit}
                          onChange={(e) => {
                            setEmailLogLimit(parseInt(e.target.value));
                            setEmailLogPage(1);
                          }}
                          className="bg-white border border-zinc-200 rounded-lg text-[10px] font-bold px-3 py-1.5 outline-none focus:border-black uppercase tracking-widest cursor-pointer"
                        >
                          <option value={10}>10 Logs</option>
                          <option value={50}>50 Logs</option>
                          <option value={100}>100 Logs</option>
                        </select>
                      </div>
                    </div>
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase font-bold text-zinc-400 tracking-widest">
                          <th className="p-6">{mailView === 'outbound' ? 'Recipient' : 'Sender'}</th>
                          <th className="p-6">Subject</th>
                          <th className="p-6">Status</th>
                          <th className="p-6 text-right">Date Sent</th>
                          <th className="p-6 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 italic-none">
                        {mailView === 'outbound' ? data.emailLogs.map(log => (
                          <tr key={log.id} className="hover:bg-zinc-50/50 transition-all group">
                            <td className="p-6">
                              <span className="font-bold text-xs text-zinc-900">{log.recipient_email}</span>
                            </td>
                            <td className="p-6 text-xs text-zinc-500 font-medium max-w-[250px] truncate">{log.subject}</td>
                            <td className="p-6">
                              <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-500 text-[9px] font-bold uppercase tracking-widest border border-zinc-200">
                                {log.status}
                              </span>
                            </td>
                            <td className="p-6 text-[11px] text-zinc-400 text-right font-bold uppercase tracking-widest">{new Date(log.sent_at).toLocaleDateString()}</td>
                            <td className="p-6 text-right">
                              <button 
                                onClick={() => setPreviewEmail({ subject: log.subject, body: log.body_html || '' })}
                                className="p-2 text-zinc-400 hover:text-black hover:bg-zinc-100 rounded-lg transition-all"
                                title="Preview Email"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        )) : data.inboundEmails.map(msg => (
                          <tr key={msg.id} className="hover:bg-zinc-50/50 transition-all group">
                            <td className="p-6 border-l-2 border-transparent group-hover:border-purple-500 transition-all">
                              <span className="font-bold text-xs text-zinc-900 uppercase tracking-tight">{msg.sender_name || 'ANONYMOUS'}</span>
                              <span className="text-[10px] text-zinc-400 mt-1 block font-bold">{msg.sender_email}</span>
                            </td>
                            <td className="p-6 text-xs text-zinc-500 font-medium max-w-[250px] truncate">{msg.subject}</td>
                            <td className="p-6 text-[10px] text-zinc-400 line-clamp-1 italic">{msg.body_text}</td>
                            <td className="p-6 text-[11px] text-zinc-400 text-right font-bold uppercase tracking-widest">{new Date(msg.received_at).toLocaleDateString()}</td>
                            <td className="p-6 text-right">
                               <button 
                                onClick={() => setPreviewEmail({ subject: msg.subject, body: msg.body_text })}
                                className="p-2 text-zinc-400 hover:text-black hover:bg-zinc-100 rounded-lg transition-all"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {mailView === 'outbound' && (
                       <div className="p-6 border-t border-zinc-100 bg-zinc-50/30 flex items-center justify-between">
                       <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                         Showing page {emailLogPage} of {Math.ceil(emailLogTotal / emailLogLimit)}
                       </div>
                       <div className="flex items-center gap-2">
                         <button 
                           disabled={emailLogPage === 1}
                           onClick={() => setEmailLogPage(p => p - 1)}
                           className="px-4 py-2 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 hover:border-black transition-all"
                         >
                           Previous
                         </button>
                         <button 
                           disabled={data.emailLogs.length < emailLogLimit || (emailLogPage * emailLogLimit >= emailLogTotal)}
                           onClick={() => setEmailLogPage(p => p + 1)}
                           className="px-4 py-2 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 hover:border-black transition-all"
                         >
                           Next
                         </button>
                       </div>
                     </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'bug-reports' && (
          <div className="space-y-8 animate-in">
            <div className="bg-white border border-zinc-950/10 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest w-[25%]">Report</th>
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest w-[35%]">Description</th>
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest">Status / Priority</th>
                    <th className="p-6 text-xs font-bold text-zinc-500 uppercase tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 italic-none">
                  {data.bugReports.map(report => (
                    <tr key={report.id} className="hover:bg-zinc-50/50 transition-all group">
                      <td className="p-6">
                        <div className="flex flex-col gap-1">
                          <span className="font-bold text-sm text-zinc-900">{report.title}</span>
                          <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">{report.email || 'Anonymous'}</span>
                          <span className="text-[9px] text-zinc-300 font-medium">{new Date(report.created_at).toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="p-6">
                        <p className="text-xs text-zinc-600 line-clamp-3 leading-relaxed" title={report.description}>
                          {report.description}
                        </p>
                        {report.browser_info && (
                           <div className="mt-2 text-[9px] text-zinc-400 font-mono bg-zinc-50 p-2 rounded border border-zinc-100 overflow-hidden truncate max-w-sm" title={report.browser_info}>
                             {report.browser_info}
                           </div>
                        )}
                      </td>
                      <td className="p-6">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                             <div className={`w-2 h-2 rounded-full ${
                               report.status === 'OPEN' ? 'bg-amber-400' : 
                               report.status === 'IN_PROGRESS' ? 'bg-cyan-400' : 
                               'bg-emerald-400'
                             }`} />
                             <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">{report.status}</span>
                          </div>
                          <div className="flex items-center gap-2">
                             <span className={`px-2 py-0.5 rounded text-[8px] font-bold uppercase tracking-widest border ${
                               report.priority === 'CRITICAL' ? 'bg-red-50 text-red-600 border-red-100' :
                               report.priority === 'HIGH' ? 'bg-orange-50 text-orange-600 border-orange-100' :
                               report.priority === 'MEDIUM' ? 'bg-zinc-50 text-zinc-600 border-zinc-200' :
                               'bg-zinc-100 text-zinc-400 border-zinc-200'
                             }`}>
                               {report.priority}
                             </span>
                          </div>
                        </div>
                      </td>
                      <td className="p-6 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <select 
                            value={report.status}
                            onChange={(e) => handleUpdateBugReport(report.id, e.target.value, report.resolution_notes || '')}
                            className="bg-white border border-zinc-200 rounded-lg text-[9px] font-bold px-2 py-1 outline-none focus:border-zinc-500 uppercase tracking-widest cursor-pointer"
                          >
                            <option value="OPEN">Open</option>
                            <option value="IN_PROGRESS">Progress</option>
                            <option value="RESOLVED">Resolved</option>
                            <option value="CLOSED">Closed</option>
                          </select>
                          <button 
                            onClick={() => {
                              const notes = window.prompt('Resolution Notes:', report.resolution_notes || '');
                              if (notes !== null) {
                                handleUpdateBugReport(report.id, report.status, notes);
                              }
                            }}
                            className="p-2 text-zinc-400 hover:text-black hover:bg-zinc-100 rounded-lg transition-all"
                            title="Edit Resolution Notes"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.bugReports.length === 0 && (
                     <tr>
                       <td colSpan={4} className="p-20 text-center text-zinc-400 italic text-sm font-bold uppercase tracking-widest">No bug reports filed.</td>
                     </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'security-logs' && (
          <div className="space-y-8 animate-in">
...
          </div>
        )}

        {activeTab === 'admin-security' && (
          <div className="space-y-8 animate-in max-w-4xl">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-zinc-950 uppercase flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-cyan-600" />
                Administrative Security
              </h2>
              <p className="text-sm text-zinc-500 font-medium italic">Manage multi-factor authentication and emergency recovery for the root admin account.</p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-900">MFA Status</h3>
                  <div className={`p-1 px-3 rounded-full text-[10px] font-bold uppercase tracking-widest ${data.adminMfaStatus?.enabled ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                    {data.adminMfaStatus?.enabled ? 'Active' : 'Missing'}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-zinc-50">
                    <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-widest">Enabled Since</span>
                    <span className="text-xs font-bold text-zinc-900">{data.adminMfaStatus?.enabledAt ? new Date(parseInt(data.adminMfaStatus.enabledAt)).toLocaleString() : 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-zinc-50">
                    <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-widest text-zinc-500">Recovery Codes Remaining</span>
                    <span className={`text-xs font-bold ${data.adminMfaStatus?.backupCodesRemaining && data.adminMfaStatus.backupCodesRemaining < 3 ? 'text-red-500' : 'text-zinc-900'}`}>
                      {data.adminMfaStatus?.backupCodesRemaining || 0} / 10
                    </span>
                  </div>
                </div>

                <div className="pt-4 space-y-4">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Current Authenticator Code</label>
                  <input 
                    type="text"
                    value={mfaTotp}
                    onChange={(e) => setMfaTotp(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-center text-lg font-mono tracking-[0.5em] outline-none focus:border-cyan-500/50 transition-all font-bold"
                  />
                  
                  <div className="grid grid-cols-1 gap-3">
                    <button 
                      onClick={() => handleMfaAction('regenerate_backup_codes', { totp: mfaTotp })}
                      disabled={mfaActionLoading || !mfaTotp || !data.adminMfaStatus?.enabled}
                      className="w-full bg-white border border-zinc-200 text-zinc-950 font-bold py-3 rounded-xl hover:border-black transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-[10px] disabled:opacity-50"
                    >
                      {mfaActionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Regenerate Backup Codes
                    </button>
                    <button 
                      onClick={() => handleMfaAction('rotate_start', { totp: mfaTotp })}
                      disabled={mfaActionLoading || (data.adminMfaStatus?.enabled && !mfaTotp) || !!rotationData}
                      className="w-full bg-zinc-950 text-white font-bold py-3 rounded-xl hover:bg-zinc-800 transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-[10px] disabled:opacity-50 shadow-lg shadow-zinc-950/10"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                      {data.adminMfaStatus?.enabled ? 'Rotate MFA Secret' : 'Enable MFA Protection'}
                    </button>
                  </div>
                </div>
                {mfaError && <p className="text-[10px] text-red-500 font-bold uppercase text-center">{mfaError}</p>}
              </div>

              <div className="space-y-6">
                {newBackupCodes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-3xl p-8 space-y-6 animate-in slide-in-from-right">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <h3 className="text-sm font-bold uppercase tracking-widest text-amber-900">New Recovery Codes</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                       {newBackupCodes.map((code, idx) => (
                         <code key={idx} className="bg-white p-2 rounded border border-amber-200 text-center font-mono text-xs font-bold text-zinc-900">
                           {code}
                         </code>
                       ))}
                    </div>
                    <p className="text-[10px] text-amber-800 italic font-medium leading-relaxed">
                      Store these safely. Your previous codes have been invalidated.
                    </p>
                    <button 
                      onClick={() => setNewBackupCodes(null)}
                      className="w-full py-2 bg-amber-200/50 hover:bg-amber-200 text-amber-900 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {rotationData && (
                  <div className="bg-cyan-50 border border-cyan-100 rounded-3xl p-8 space-y-6 animate-in slide-in-from-right">
                    <div className="text-center space-y-4">
                      <h3 className="text-sm font-bold uppercase tracking-widest text-cyan-900">Scan New QR Code</h3>
                      <div className="bg-white p-3 rounded-2xl border border-cyan-100 inline-block mx-auto">
                        <img 
                          src={rotationData.qrCodeUrl} 
                          alt="QR Code" 
                          className="w-48 h-48"
                        />
                      </div>

                      {rotationData.manualSetupCode && (
                        <div className="p-4 bg-white/50 border border-cyan-200 rounded-xl space-y-1">
                          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest block">Manual Setup Code</span>
                          <code className="text-sm font-mono font-bold text-cyan-700 select-all">{rotationData.manualSetupCode}</code>
                        </div>
                      )}

                      <div className="space-y-3">
                        <label className="text-[10px] font-bold text-cyan-700 uppercase tracking-widest block">Verify NEW Code</label>
                        <input 
                          type="text"
                          value={mfaNewTotp}
                          onChange={(e) => setMfaNewTotp(e.target.value)}
                          placeholder="000000"
                          maxLength={6}
                          className="w-full bg-white border border-cyan-200 rounded-xl px-5 py-3 text-center text-lg font-mono tracking-[0.5em] outline-none focus:border-cyan-500 transition-all font-bold"
                        />
                        <button 
                          onClick={() => handleMfaAction('rotate_confirm', { totp: mfaTotp, new_totp: mfaNewTotp })}
                          disabled={mfaActionLoading || !mfaNewTotp}
                          className="w-full bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 transition-all uppercase tracking-widest text-[10px]"
                        >
                          Confirm Rotation
                        </button>
                        <button 
                          onClick={() => setRotationData(null)}
                          className="w-full py-2 text-cyan-600 hover:text-cyan-800 text-[10px] font-bold uppercase tracking-widest"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {!newBackupCodes && !rotationData && (
                  <div className="bg-zinc-50 border border-zinc-100 rounded-3xl p-8 flex flex-col items-center text-center justify-center h-full">
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center border border-zinc-200 shadow-sm mb-4">
                      <Lock className="w-8 h-8 text-zinc-200" />
                    </div>
                    <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest px-8">
                       Select an action to secure your root administrative access
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-red-50/30 border border-red-100 rounded-3xl p-8 flex gap-6">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-red-200 shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="space-y-2">
                 <h4 className="font-bold text-red-900 uppercase tracking-tight">Security Warning</h4>
                 <p className="text-xs text-red-800/70 font-medium leading-relaxed">
                   Root admin credentials grant full control over the database, users, and financial records. 
                   Never share your MFA secret and always keep physical or encrypted backups of your recovery codes.
                   If you lose both your MFA device and recovery codes, the only way to regain access is a manual database operation by an infrastructure engineer.
                 </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'app-updates' && (
          <div className="space-y-8 animate-in">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold tracking-tight text-zinc-950 uppercase flex items-center gap-3">
                  <Rocket className="w-6 h-6 text-cyan-600" />
                  Application Updates
                </h2>
                <p className="text-sm text-zinc-500 font-medium italic">Share new features, bug fixes, and progress with your users.</p>
              </div>
              <button 
                onClick={() => setEditingUpdate({ 
                  title: '', 
                  content: '', 
                  category: 'FEATURE', 
                  is_published: 1 
                })}
                className="bg-black text-white px-6 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-all active:scale-95 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Update
              </button>
            </div>

            {editingUpdate && (
              <div className="bg-white border border-zinc-950/10 rounded-2xl p-8 shadow-sm space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400">
                    {editingUpdate.id ? 'Edit Update' : 'Create New Update'}
                  </h3>
                  <button onClick={() => setEditingUpdate(null)} className="text-zinc-400 hover:text-black">
                    <Plus className="w-6 h-6 rotate-45" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-6">
                  <div className="col-span-2 space-y-2">
                    <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Update Title</label>
                    <input 
                      value={editingUpdate.title || ''}
                      onChange={(e) => setEditingUpdate({ ...editingUpdate, title: e.target.value })}
                      placeholder="e.g. New Particle Engine v2.0"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-sm outline-none focus:border-cyan-500/50 transition-all font-bold"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Category</label>
                    <select 
                      value={editingUpdate.category || 'FEATURE'}
                      onChange={(e) => setEditingUpdate({ ...editingUpdate, category: e.target.value as any })}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-sm outline-none focus:border-cyan-500/50 transition-all font-bold uppercase tracking-widest"
                    >
                      <option value="FEATURE">Feature</option>
                      <option value="FIX">Bug Fix</option>
                      <option value="IMPROVEMENT">Improvement</option>
                      <option value="NEWS">News</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Version (Optional)</label>
                    <input 
                      value={editingUpdate.version || ''}
                      onChange={(e) => setEditingUpdate({ ...editingUpdate, version: e.target.value })}
                      placeholder="e.g. 1.0.4"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-sm outline-none focus:border-cyan-500/50 transition-all font-bold"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Status</label>
                    <div className="flex items-center gap-4 py-3">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={editingUpdate.is_published === 1}
                          onChange={(e) => setEditingUpdate({ ...editingUpdate, is_published: e.target.checked ? 1 : 0 })}
                          className="w-4 h-4 rounded border-zinc-300 text-black focus:ring-black"
                        />
                        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500 group-hover:text-black transition-colors">Publish Immediately</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest px-1">Content (Supports Markdown)</label>
                  <textarea 
                    value={editingUpdate.content || ''}
                    onChange={(e) => setEditingUpdate({ ...editingUpdate, content: e.target.value })}
                    rows={8}
                    placeholder="Describe what changed..."
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-4 text-sm outline-none focus:border-cyan-500/30 transition-all font-medium leading-relaxed"
                  />
                </div>

                <div className="flex justify-end gap-4 pt-4 border-t border-zinc-50">
                   <button 
                    onClick={() => setEditingUpdate(null)}
                    className="px-6 py-2.5 text-[10px] font-bold text-zinc-400 hover:text-zinc-600 uppercase tracking-widest transition-all"
                  >
                    Discard
                  </button>
                  <button 
                    onClick={handleSaveUpdate}
                    disabled={isSavingUpdate}
                    className="px-8 py-2.5 bg-zinc-950 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest flex items-center gap-3 hover:bg-zinc-800 transition-all disabled:opacity-50 shadow-lg shadow-zinc-950/20"
                  >
                    {isSavingUpdate ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Save Update
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white border border-zinc-950/10 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase font-bold text-zinc-400 tracking-widest">
                    <th className="p-6">Update Details</th>
                    <th className="p-6">Category</th>
                    <th className="p-6">Status</th>
                    <th className="p-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 italic-none">
                  {data.updates.map(update => (
                    <tr key={update.id} className="hover:bg-zinc-50/50 transition-all group">
                      <td className="p-6">
                        <div className="flex flex-col gap-1">
                          <span className="font-bold text-sm text-zinc-900 group-hover:text-cyan-600 transition-colors uppercase tracking-tight">{update.title}</span>
                          <div className="flex items-center gap-2">
                             {update.version && <span className="text-[8px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 font-bold border border-zinc-200">{update.version}</span>}
                             <span className="text-[9px] text-zinc-400 font-medium uppercase tracking-widest">{new Date(update.created_at * 1000).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-6">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest border ${
                          update.category === 'FEATURE' ? 'bg-cyan-50 text-cyan-600 border-cyan-100' :
                          update.category === 'FIX' ? 'bg-red-50 text-red-600 border-red-100' :
                          update.category === 'IMPROVEMENT' ? 'bg-purple-50 text-purple-600 border-purple-100' :
                          'bg-zinc-50 text-zinc-600 border-zinc-200'
                        }`}>
                          {update.category}
                        </span>
                      </td>
                      <td className="p-6">
                        <div className="flex items-center gap-2">
                           <div className={`w-1.5 h-1.5 rounded-full ${update.is_published ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                           <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{update.is_published ? 'Published' : 'Draft'}</span>
                        </div>
                      </td>
                      <td className="p-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                           <button 
                            onClick={() => setEditingUpdate(update)}
                            className="p-2 text-zinc-300 hover:text-black hover:bg-zinc-100 rounded-lg transition-all"
                            title="Edit"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteUpdate(update.id)}
                            className="p-2 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete"
                          >
                            <AlertCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.updates.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-20 text-center text-zinc-400 italic text-sm font-bold uppercase tracking-widest">No updates posted yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'reconciliation' && (
          <div className="space-y-8 animate-in">
            <div className="bg-white border border-zinc-950/10 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl font-bold tracking-tight text-zinc-950 uppercase flex items-center gap-3">
                    <RefreshCw className={`w-6 h-6 text-cyan-600 ${isReconciling ? 'animate-spin' : ''}`} />
                    Subscription Reconciliation
                  </h2>
                  <p className="text-sm text-zinc-500 font-medium italic mt-2">
                    Compares local records with live PayPal data to repair mismatches and fix missed webhooks.
                  </p>
                </div>
                <button 
                  onClick={handleRunReconciliation}
                  disabled={isReconciling}
                  className="bg-black text-white px-8 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-all active:scale-95 flex items-center gap-3 disabled:opacity-50 shadow-xl shadow-zinc-950/20"
                >
                  {isReconciling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  Run Global Reconciliation
                </button>
              </div>

              {reconcileResults ? (
                <div className="grid grid-cols-4 gap-6 mb-8">
                  <div className="bg-zinc-50 border border-zinc-100 p-6 rounded-2xl">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-1">Total Checked</span>
                    <span className="text-2xl font-bold text-zinc-950">{reconcileResults.total}</span>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-2xl">
                    <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest block mb-1">Repaired</span>
                    <span className="text-2xl font-bold text-emerald-700">{reconcileResults.updated}</span>
                  </div>
                  <div className="bg-zinc-50 border border-zinc-100 p-6 rounded-2xl">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-1">In Sync</span>
                    <span className="text-2xl font-bold text-zinc-950">{reconcileResults.skipped}</span>
                  </div>
                  <div className="bg-red-50 border border-red-100 p-6 rounded-2xl">
                    <span className="text-[10px] font-bold text-red-600 uppercase tracking-widest block mb-1">Errors</span>
                    <span className="text-2xl font-bold text-red-700">{reconcileResults.errors}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-cyan-50/30 border border-cyan-100 p-8 rounded-2xl flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center border border-cyan-100 shadow-sm mb-4">
                    <Activity className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-2 uppercase tracking-tight">Ready for Reconciliation</h3>
                  <p className="text-sm text-zinc-500 max-w-md font-medium leading-relaxed">
                    Start a reconciliation job to verify the integrity of all PayPal subscriptions. This will not charge users or interrupt active sessions.
                  </p>
                </div>
              )}

              {reconcileResults?.details && reconcileResults.details.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-4 px-2">Recent Job Audit Log</h3>
                  <div className="bg-white border border-zinc-100 rounded-2xl overflow-hidden shadow-sm">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-zinc-50 border-b border-zinc-100 text-[9px] font-bold text-zinc-400 uppercase tracking-widest">
                        <tr>
                          <th className="p-4">User ID</th>
                          <th className="p-4">Event</th>
                          <th className="p-4">Status Change</th>
                          <th className="p-4 text-right">PayPal State</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-50">
                        {reconcileResults.details.map((detail: any, i: number) => (
                          <tr key={i} className="text-xs">
                            <td className="p-4 font-mono text-[10px] text-zinc-400 whitespace-nowrap">{detail.user_id}</td>
                            <td className="p-4 uppercase tracking-widest font-bold text-[9px]">
                              {detail.error ? (
                                <span className="text-red-500">Error: {detail.error}</span>
                              ) : (
                                <span className={detail.new_status ? 'text-emerald-600' : 'text-zinc-400'}>
                                  {detail.new_status ? 'Repaired' : 'Checked'}
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              {detail.old_status && detail.new_status ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-zinc-300 line-through">{detail.old_status}</span>
                                  <ArrowRight className="w-3 h-3 text-zinc-400" />
                                  <span className="font-bold text-zinc-900 uppercase tracking-widest text-[10px]">{detail.new_status}</span>
                                </div>
                              ) : '-'}
                            </td>
                            <td className="p-4 text-right">
                              <span className="bg-zinc-100 px-2 py-1 rounded text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                                {detail.paypal_status || 'Unknown'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-8 flex gap-6 shadow-sm overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-100/50 blur-3xl pointer-events-none" />
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-amber-200 shadow-sm shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-amber-900 uppercase tracking-tight mb-2">Operational Guidelines</h4>
                <p className="text-sm text-amber-800/70 font-medium leading-relaxed max-w-3xl">
                  Reconciliation should be run daily or after any major platform downtime to ensure subscription parity. 
                  This process uses the PayPal REST API as the absolute source of truth. If a subscription is cancelled in PayPal but 
                  remains active in our system beyond its paid period, it will be automatically downgraded to the FREE plan.
                </p>
                <div className="mt-4 flex items-center gap-4">
                  <div className="flex items-center gap-2 text-[10px] font-bold text-amber-700 uppercase tracking-widest">
                    <Check className="w-3 h-3" /> Fixes Missing Webhooks
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-amber-700 uppercase tracking-widest">
                    <Check className="w-3 h-3" /> Repairs State Drift
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-amber-700 uppercase tracking-widest">
                    <Check className="w-3 h-3" /> Audits All Revenue Channels
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 flex items-center justify-between shadow-xl">
              <div className="flex gap-6 items-center">
                <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center border border-zinc-800 shadow-inner shrink-0">
                  <Database className="w-6 h-6 text-cyan-500" />
                </div>
                <div>
                  <h4 className="font-bold text-white uppercase tracking-tight mb-1">Database Schema Management</h4>
                  <p className="text-xs text-zinc-400 font-medium leading-relaxed max-w-xl">
                    Manually trigger a schema migration to ensure all required tables and indexes exist. 
                    This handles the first-run initialization and updates for new features.
                  </p>
                </div>
              </div>
              <button 
                onClick={handleMigrateDatabase}
                disabled={isMigrating}
                className="bg-white text-black px-8 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-200 transition-all active:scale-95 flex items-center gap-3 disabled:opacity-50"
              >
                {isMigrating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                Initialize Tables
              </button>
            </div>
          </div>
        )}
        {activeTab === 'email-templates' && (
          <div className="space-y-8 animate-in">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-zinc-950 uppercase flex items-center gap-3">
                <FileText className="w-6 h-6 text-cyan-600" />
                Email Templates
              </h2>
              <p className="text-sm text-zinc-500 font-medium italic">Preview system-generated emails sent to users.</p>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {emailTemplates.map(template => (
                <div key={template.id} className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm hover:border-black transition-all flex flex-col">
                  <div className="p-6 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-zinc-50 border border-zinc-100 flex items-center justify-center mb-4">
                      <Mail className="w-6 h-6 text-zinc-400" />
                    </div>
                    <h3 className="font-bold text-zinc-900 mb-1 uppercase tracking-tight">{template.name}</h3>
                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-4">Subject: {template.subject}</p>
                    <div className="bg-zinc-50 rounded-lg p-4 h-40 overflow-hidden border border-zinc-100 relative grayscale group-hover:grayscale-0 transition-all">
                      <div className="scale-[0.25] origin-top-left w-[400%] h-[400%] pointer-events-none" dangerouslySetInnerHTML={{ __html: template.html }} />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-50 to-transparent" />
                    </div>
                  </div>
                  <div className="p-4 bg-zinc-50 border-t border-zinc-100 flex justify-end">
                    <button 
                      onClick={() => setPreviewEmail({ subject: template.subject, body: template.html })}
                      className="px-6 py-2 bg-black text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-all flex items-center gap-2"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View Template
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'showcase' && (
          <div className="space-y-8 animate-in">
             <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold tracking-tight text-zinc-950 uppercase flex items-center gap-3">
                  <Eye className="w-6 h-6 text-cyan-600" />
                  Showcase Management
                </h2>
                <p className="text-sm text-zinc-500 font-medium italic">Manage GIFs and linked YouTube/Instagram videos for the explore section.</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-white border border-zinc-200 p-1 rounded-xl">
                  <input 
                    type="text" 
                    placeholder="YouTube or Instagram URL" 
                    value={newShowcaseUrl} 
                    onChange={(e) => setNewShowcaseUrl(e.target.value)}
                    className="px-4 py-2 text-xs outline-none w-64"
                  />
                  <select 
                    value={newShowcaseType} 
                    onChange={(e) => setNewShowcaseType(e.target.value as any)}
                    className="text-[10px] font-bold uppercase tracking-widest bg-zinc-50 px-2 py-2 rounded-lg outline-none cursor-pointer"
                  >
                    <option value="YOUTUBE">YouTube</option>
                    <option value="INSTAGRAM">Instagram</option>
                  </select>
                  <button 
                    onClick={handleShowcaseAddLink}
                    disabled={isAddingShowcaseLink || !newShowcaseUrl}
                    className="bg-black text-white px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-all disabled:opacity-50"
                  >
                    {isAddingShowcaseLink ? 'Adding...' : 'Add Link'}
                  </button>
                </div>
                <label className="bg-cyan-600 text-white px-6 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-cyan-700 transition-all active:scale-95 flex items-center gap-2 cursor-pointer shadow-sm">
                  <Plus className="w-4 h-4" />
                  Upload File
                  <input type="file" className="hidden" accept="image/gif,video/mp4" onChange={handleShowcaseUpload} />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {data.showcaseGifs.map((item) => (
                <div key={item.id || item.key} className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm group relative">
                  <div className="aspect-video bg-zinc-100 flex items-center justify-center relative overflow-hidden">
                    {item.type === 'YOUTUBE' ? (
                       <div className="w-full h-full bg-zinc-950 flex flex-col items-center justify-center p-4">
                          <Globe className="w-8 h-8 text-white/20 mb-2" />
                          <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest text-center truncate w-full">{item.url}</span>
                       </div>
                    ) : item.type === 'INSTAGRAM' ? (
                      <div className="w-full h-full bg-pink-50 flex flex-col items-center justify-center p-4">
                          <Activity className="w-8 h-8 text-pink-200 mb-2" />
                          <span className="text-[10px] text-pink-400 font-bold uppercase tracking-widest text-center truncate w-full">{item.url}</span>
                       </div>
                    ) : (
                      <img src={item.url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    )}
                    
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 backdrop-blur-[2px]">
                       <button 
                        onClick={() => handleShowcaseDelete(item.id || item.key)}
                        className="p-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all scale-90 group-hover:scale-100 flex items-center justify-center"
                        title="Delete Item"
                       >
                         <Trash2 className="w-5 h-5" />
                       </button>
                       {item.url && (
                         <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noreferrer"
                          className="p-3 bg-white text-black rounded-full hover:bg-zinc-100 transition-all scale-90 group-hover:scale-100 flex items-center justify-center"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                       )}
                    </div>
                  </div>
                  <div className="p-4 border-t border-zinc-50 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-zinc-950 uppercase tracking-widest">{item.type || 'FILE'}</span>
                      <p className="text-[9px] font-mono text-zinc-400 truncate max-w-[150px] mt-0.5">{item.key || item.url}</p>
                    </div>
                    <span className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest">{new Date(item.created_at || Date.now()).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
              {data.showcaseGifs.length === 0 && (
                <div className="col-span-full p-20 text-center bg-white border border-dashed border-zinc-200 rounded-3xl">
                   <p className="text-zinc-400 font-bold uppercase tracking-widest text-sm">No showcase items found.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Email Preview Modal */}
      {previewEmail && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white border border-zinc-950 rounded-3xl w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-300">
            <header className="p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-zinc-950 uppercase tracking-tight">Email Preview</h2>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{previewEmail.subject}</p>
                </div>
              </div>
              <button 
                onClick={() => setPreviewEmail(null)}
                className="p-2 hover:bg-zinc-100 rounded-xl transition-all"
              >
                <Plus className="w-6 h-6 rotate-45 text-zinc-400" />
              </button>
            </header>
            <div className="flex-1 bg-zinc-100 p-8 overflow-y-auto overflow-x-hidden">
               <div className="max-w-2xl mx-auto bg-white shadow-xl rounded-2xl overflow-hidden border border-zinc-200">
                  <div className="bg-zinc-50 p-4 border-b border-zinc-200 flex items-center gap-4 text-xs font-medium text-zinc-400">
                    <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-400" /><div className="w-2.5 h-2.5 rounded-full bg-amber-400" /><div className="w-2.5 h-2.5 rounded-full bg-emerald-400" /></div>
                    <div className="flex-1 bg-white border border-zinc-200 rounded px-3 py-1 truncate">Inbox &rsaquo; {previewEmail.subject}</div>
                  </div>
                  <div 
                    className="email-iframe-container"
                    dangerouslySetInnerHTML={{ __html: previewEmail.body }} 
                  />
               </div>
            </div>
          </div>
        </div>
      )}

      {/* User Detail & Delete Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div 
            className="bg-white border border-zinc-950 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <header className="p-8 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl bg-white border border-zinc-950/10 flex items-center justify-center overflow-hidden shadow-sm">
                  {selectedUser.picture ? (
                    <img src={selectedUser.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-8 h-8 text-black" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-display font-bold tracking-tight text-zinc-950 uppercase">{selectedUser.name || selectedUser.username || 'Anonymous'}</h2>
                    <div className="flex items-center gap-2">
                       <span className={getPlanStyle(selectedUser.plan)}>{selectedUser.plan}</span>
                       {selectedUser.is_suspended ? (
                         <span className="px-2 py-0.5 rounded text-[8px] font-bold bg-red-100 text-red-600 border border-red-200 uppercase tracking-widest">Suspended</span>
                       ) : (
                         <span className="px-2 py-0.5 rounded text-[8px] font-bold bg-emerald-100 text-emerald-600 border border-emerald-200 uppercase tracking-widest">Active</span>
                       )}
                    </div>
                  </div>
                  <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mt-1">{selectedUser.email}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedUser(null)}
                className="p-2 hover:bg-zinc-100 rounded-xl transition-all"
              >
                <Plus className="w-6 h-6 rotate-45 text-zinc-400" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-8 bg-white space-y-12">
              {/* Core Metadata */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100 flex items-center gap-4">
                   <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-cyan-600" />
                   </div>
                   <div>
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Country</span>
                      <span className="text-sm font-bold text-zinc-900">{selectedUser.country_code || 'N/A'}</span>
                   </div>
                </div>
                <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100 flex items-center gap-4 col-span-2">
                   <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-purple-600" />
                   </div>
                   <div className="truncate">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Browser / Device</span>
                      <span className="text-sm font-bold text-zinc-900 truncate block" title={selectedUser.device_info}>{selectedUser.device_info || 'N/A'}</span>
                   </div>
                </div>
                <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100 flex items-center gap-4">
                   <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-pink-600" />
                   </div>
                   <div>
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Studio Time</span>
                      <span className="text-sm font-bold text-zinc-900">{Math.floor((selectedUser.studio_time || 0) / 3600)}h {Math.floor(((selectedUser.studio_time || 0) % 3600) / 60)}m</span>
                   </div>
                </div>
              </div>

              {/* Core Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-2">Registration</span>
                  <div className="text-lg font-bold text-zinc-950">{new Date(selectedUser.created_at).toLocaleDateString()}</div>
                  <div className="text-[10px] text-zinc-400 font-medium">{new Date(selectedUser.created_at).toLocaleTimeString()}</div>
                </div>
                <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-2">Total Score</span>
                  <div className="text-lg font-bold text-zinc-950">{selectedUser.total_score || 0}</div>
                  <div className="text-[10px] text-zinc-400 font-medium">Accumulated Points</div>
                </div>
                <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-2">Total Play Time</span>
                  <div className="text-lg font-bold text-zinc-950">{Math.floor((selectedUser.total_play_time || 0) / 60)}m {(selectedUser.total_play_time || 0) % 60}s</div>
                  <div className="text-[10px] text-zinc-400 font-medium">Simulation Active Time</div>
                </div>
              </div>

              {/* NEW Access Management Section */}
              <section className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-900 flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-cyan-600" />
                      Access Management (Trials & Beta)
                    </h3>
                    <p className="text-[11px] text-zinc-500 font-medium mt-1">Override subscriptions with temporary trial or beta access.</p>
                  </div>
                  {selectedUser.access_mode && selectedUser.access_mode !== 'normal' && (
                    <div className="px-3 py-1 bg-cyan-50 border border-cyan-100 rounded-full flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse" />
                      <span className="text-[10px] font-bold text-cyan-700 uppercase tracking-widest">Active Override: {selectedUser.access_mode.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-6 items-end">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Access Type</label>
                    <select 
                      value={accessUpdateData.mode}
                      onChange={(e) => setAccessUpdateData({ ...accessUpdateData, mode: e.target.value })}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm font-bold outline-none focus:border-cyan-500/50 uppercase transition-all"
                    >
                      <option value="normal">Normal / Paid</option>
                      <option value="trial_creator_pro">Creator Pro Trial</option>
                      <option value="trial_studio_elite">Studio Elite Trial</option>
                      <option value="beta_tester">Beta Tester</option>
                    </select>
                  </div>

                  {accessUpdateData.mode !== 'normal' && (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Duration</label>
                      <select 
                        value={accessUpdateData.duration}
                        onChange={(e) => setAccessUpdateData({ ...accessUpdateData, duration: e.target.value })}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm font-bold outline-none focus:border-cyan-500/50 uppercase transition-all"
                      >
                        <option value="3">3 Days</option>
                        <option value="7">7 Days</option>
                        <option value="30">30 Days</option>
                        <option value="90">90 Days</option>
                        <option value="unlimited">Unlimited (No Expiry)</option>
                        <option value="custom">Custom Date</option>
                      </select>
                    </div>
                  )}

                  {accessUpdateData.mode !== 'normal' && accessUpdateData.duration === 'custom' && (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Expiration Date</label>
                      <input 
                        type="datetime-local"
                        value={accessUpdateData.customExpiresAt}
                        onChange={(e) => setAccessUpdateData({ ...accessUpdateData, customExpiresAt: e.target.value })}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2 text-sm font-bold outline-none focus:border-cyan-500/50 transition-all"
                      />
                    </div>
                  )}

                  <div className={`${accessUpdateData.mode === 'normal' ? 'col-span-3' : accessUpdateData.duration === 'custom' ? 'col-span-1' : 'col-span-2'} flex justify-end`}>
                    <button 
                      onClick={handleUpdateAccess}
                      disabled={isUpdatingAccess}
                      className="bg-zinc-950 text-white font-bold py-2.5 px-6 rounded-xl hover:bg-zinc-800 transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-[10px] shadow-lg shadow-zinc-950/10 disabled:opacity-50 h-[42px] min-w-[160px]"
                    >
                      {isUpdatingAccess ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      Update Access
                    </button>
                  </div>
                </div>

                {selectedUser.access_mode && selectedUser.access_mode !== 'normal' && (
                  <div className="mt-8 pt-6 border-t border-zinc-100 flex flex-wrap items-center gap-8">
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-zinc-400" />
                        </div>
                        <div>
                          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest block">Expires At</span>
                          <span className="text-xs font-bold text-zinc-900">
                            {(() => {
                              const exp = selectedUser.access_mode === 'beta_tester' ? selectedUser.beta_expires_at : selectedUser.access_expires_at;
                              return exp ? new Date(exp * 1000).toLocaleString() : 'Unlimited (No Expiry)';
                            })()}
                          </span>
                        </div>
                     </div>
                     <div className="flex items-center gap-3">
                        {(() => {
                          const exp = selectedUser.access_mode === 'beta_tester' ? selectedUser.beta_expires_at : selectedUser.access_expires_at;
                          if (!exp) {
                            return (
                              <>
                                <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div>
                                  <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest block">Status</span>
                                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Active (Unlimited)</span>
                                </div>
                              </>
                            );
                          }
                          const now = Math.floor(Date.now() / 1000);
                          const remainingSeconds = exp - now;
                          if (remainingSeconds <= 0) {
                            return (
                              <>
                                <div className="w-8 h-8 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center">
                                  <Clock className="w-4 h-4 text-red-600" />
                                </div>
                                <div>
                                  <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest block">Status</span>
                                  <span className="text-xs font-bold text-red-600 uppercase tracking-widest">Expired</span>
                                </div>
                              </>
                            );
                          }
                          const remainingDays = Math.floor(remainingSeconds / (24 * 3600));
                          const remainingHours = Math.floor((remainingSeconds % (24 * 3600)) / 3600);
                          return (
                            <>
                              <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                                <Activity className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div>
                                <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest block">Time Remaining</span>
                                <span className={`text-xs font-bold ${remainingSeconds < 86400 ? 'text-red-500' : 'text-emerald-600'}`}>
                                  {remainingDays > 0 ? `${remainingDays}d ${remainingHours}h` : `${remainingHours}h`}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                     </div>

                     {selectedUser.access_mode === 'beta_tester' && selectedUser.beta_granted_by && (
                       <div className="flex items-center gap-3">
                         <div className="w-8 h-8 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center">
                           <Users className="w-4 h-4 text-zinc-400" />
                         </div>
                         <div>
                           <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest block">Granted By</span>
                           <span className="text-xs font-bold text-zinc-900 uppercase">
                             {selectedUser.beta_granted_by}
                           </span>
                         </div>
                       </div>
                     )}
                  </div>
                )}
              </section>


              {/* Mode-specific Stats */}
              {userModeStats.length > 0 && (
                <section>
                   <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-6 flex items-center gap-3">
                    <BarChart3 className="w-4 h-4" />
                    Simulation Mode Distribution
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    {userModeStats.map(stat => (
                      <div key={stat.mode} className="bg-white border border-zinc-100 rounded-2xl p-5 shadow-sm hover:border-cyan-200 transition-all">
                        <div className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mb-1">{stat.mode.replace('_', ' ')}</div>
                        <div className="text-lg font-bold text-zinc-900">{Math.floor(stat.time_spent / 60)}m {stat.time_spent % 60}s</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Payment History Section */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-6 flex items-center gap-3">
                  <History className="w-4 h-4" />
                  Financial History
                </h3>
                <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
                  {isPaymentLoading ? (
                    <div className="p-12 flex flex-col items-center justify-center gap-4">
                      <RefreshCw className="w-6 h-6 text-zinc-200 animate-spin" />
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Fetching ledger...</span>
                    </div>
                  ) : userPayments.length > 0 ? (
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-zinc-50/50 border-b border-zinc-100">
                          <th className="p-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest pl-8">Order ID</th>
                          <th className="p-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Plan</th>
                          <th className="p-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Amount</th>
                          <th className="p-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Status</th>
                          <th className="p-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest pr-8 text-right">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-50">
                        {userPayments.map(p => (
                           <tr key={p.id} className="hover:bg-zinc-50/50 transition-colors text-sm">
                             <td className="p-4 pl-8">
                               <div className="font-mono text-[10px] text-zinc-500">{p.id.substring(0, 12)}...</div>
                               <div className="text-[9px] text-zinc-400 mt-0.5">Paypal: {p.paypal_order_id || 'N/A'}</div>
                             </td>
                             <td className="p-4 font-bold text-zinc-900 text-xs uppercase">{p.plan_id}</td>
                             <td className="p-4 font-bold text-zinc-900 text-xs">{p.amount} {p.currency}</td>
                             <td className="p-4">
                               <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest border ${
                                 p.status === 'COMPLETED' 
                                   ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                                   : 'bg-zinc-100 text-zinc-400 border-zinc-200'
                               }`}>
                                 {p.status}
                               </span>
                             </td>
                             <td className="p-4 pr-8 text-right">
                               <div className="flex flex-col items-end gap-2">
                                 <div className="text-xs text-zinc-400 font-bold uppercase tracking-widest leading-none">
                                   {new Date(p.created_at).toLocaleDateString()}
                                 </div>
                                 <div className="text-[9px] text-zinc-300 font-medium leading-none">
                                   {new Date(p.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                 </div>
                                 <div className="flex items-center gap-2 mt-1">
                                    <a 
                                      href={`/api/payments/invoice/${p.id}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-zinc-300 hover:text-black transition-colors"
                                      title="View Invoice"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                    </a>
                                    {p.paypal_order_id && (
                                      <a 
                                        href={`https://www.paypal.com/activity/payment/${p.paypal_order_id}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-zinc-300 hover:text-cyan-600 transition-colors"
                                        title="View on PayPal"
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                      </a>
                                    )}
                                 </div>
                               </div>
                             </td>
                           </tr>
                         ))}
                       </tbody>
                    </table>
                  ) : (
                    <div className="p-12 text-center">
                      <p className="text-zinc-300 text-sm font-bold uppercase tracking-widest">No transaction records found</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Danger Zone */}
              <section className="pt-12 border-t border-zinc-100">
                <div className="bg-amber-50/30 border border-amber-100 rounded-3xl p-8 mb-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-amber-600 font-bold flex items-center gap-2 mb-2">
                         <Lock className="w-5 h-5" />
                         Account Restriction
                      </h4>
                      <p className="text-sm text-amber-900/60 font-medium leading-relaxed max-w-xl">
                        Suspending a user blocks their access to the creator studio and simulation dashboard immediately. They will still be able to see the home page but cannot log in or save data.
                      </p>
                    </div>
                    <button 
                       onClick={() => handleToggleSuspension(selectedUser.id, !!selectedUser.is_suspended)}
                       className={`px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
                         selectedUser.is_suspended 
                           ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                           : 'bg-amber-600 text-white hover:bg-amber-700'
                       }`}
                    >
                       {selectedUser.is_suspended ? 'Reactivate Account' : 'Suspend Account'}
                    </button>
                  </div>
                </div>

                <div className="bg-red-50/30 border border-red-100 rounded-3xl p-8">
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <h4 className="text-red-600 font-bold flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5" />
                        Danger Zone
                      </h4>
                      <p className="text-sm text-red-900/60 font-medium leading-relaxed max-w-xl">
                        Deleting this user will permanently remove their profile, all saved simulations, purchase records, and custom sounds. This action is irreversible.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] font-bold text-red-900 uppercase tracking-widest block px-1">
                      To confirm, type <span className="font-black text-red-600 select-all">{selectedUser.email}</span> below:
                    </label>
                    <div className="flex gap-4">
                      <input 
                        type="text"
                        value={deleteConfirmEmail}
                        onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                        placeholder="Paste email here..."
                        className="flex-1 bg-white border border-red-200 rounded-xl px-5 py-3 text-sm outline-none focus:border-red-500 font-bold text-red-900 placeholder:text-red-200"
                      />
                      <button 
                        onClick={handleDeleteUser}
                        disabled={deleteConfirmEmail !== selectedUser.email || isDeletingUser}
                        className="px-8 py-3 bg-red-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-red-700 active:scale-95 transition-all disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed flex items-center gap-3 shadow-lg shadow-red-600/20"
                      >
                        {isDeletingUser ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                        Execute Deletion
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NavItem: React.FC<{ icon: React.ReactNode, label: string, active: boolean, onClick: () => void }> = ({ icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-6 py-3.5 rounded-xl transition-all font-bold text-[11px] uppercase tracking-widest group relative overflow-hidden ${
      active 
        ? 'bg-zinc-950 text-white shadow-lg shadow-zinc-950/10' 
        : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100'
    }`}
  >
    {React.isValidElement(icon) && React.cloneElement(icon as React.ReactElement<any>, { className: `w-4 h-4 ${active ? 'text-white' : 'group-hover:text-cyan-600 transition-colors'}` })}
    {label}
  </button>
);

const getPlanStyle = (plan: string) => {
  switch (plan) {
    case 'FREE': return 'bg-zinc-100 text-zinc-500 border-zinc-200 border uppercase font-bold text-[10px] px-2.5 py-1 rounded-lg tracking-widest';
    case 'PRO': return 'bg-cyan-50 text-cyan-600 border-cyan-100 border uppercase font-bold text-[10px] px-2.5 py-1 rounded-lg tracking-widest';
    case 'ELITE': return 'bg-purple-50 text-purple-600 border-purple-100 border uppercase font-bold text-[10px] px-2.5 py-1 rounded-lg tracking-widest';
    case 'ENTERPRISE': return 'bg-amber-50 text-amber-600 border-amber-100 border uppercase font-bold text-[10px] px-2.5 py-1 rounded-lg tracking-widest';
    default: return 'bg-zinc-100 text-zinc-600 uppercase font-bold text-[10px] px-2.5 py-1 rounded-lg tracking-widest';
  }
};

const MailTabButton: React.FC<{ icon: React.ReactNode, label: string, active: boolean, onClick: () => void, count?: number }> = ({ icon, label, active, onClick, count }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center justify-between px-5 py-4 rounded-xl transition-all group border ${active ? 'bg-zinc-50 text-zinc-950 border-zinc-200' : 'text-zinc-400 hover:text-zinc-600 border-transparent hover:bg-zinc-50/50'}`}
  >
    <div className="flex items-center gap-4">
      {React.isValidElement(icon) && React.cloneElement(icon as React.ReactElement<any>, { className: `w-4 h-4 ${active ? 'text-cyan-600' : 'group-hover:text-cyan-500/50 transition-colors'}` })}
      <span className="text-[11px] font-bold uppercase tracking-widest">{label}</span>
    </div>
    {count !== undefined && (
      <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold ${active ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-400'}`}>
        {count}
      </span>
    )}
  </button>
);

const StatCard: React.FC<{ label: string, value: number | string, icon: React.ReactNode, color: 'cyan' | 'purple' | 'pink' }> = ({ label, value, icon, color }) => {
  const colorMap = {
    cyan: '#06b6d4',
    purple: '#a855f7',
    pink: '#ec4899'
  };
  
  const selectedColor = colorMap[color];

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6 relative overflow-hidden group shadow-sm transition-all hover:shadow-md">
      <div 
        className="absolute top-0 right-0 w-32 h-32 blur-3xl opacity-5 group-hover:opacity-10 transition-all pointer-events-none"
        style={{ backgroundColor: selectedColor }}
      />
      <div className="flex items-center gap-4 mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center border transition-all"
          style={{ 
            backgroundColor: `${selectedColor}10`, 
            borderColor: `${selectedColor}20`,
            color: selectedColor
          }}
        >
          {React.isValidElement(icon) && React.cloneElement(icon as React.ReactElement<any>, { className: 'w-6 h-6' })}
        </div>
        <div>
          <h4 className="text-3xl font-bold tracking-tight text-zinc-950">{value}</h4>
        </div>
      </div>
      <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest">{label}</p>
    </div>
  );
};

const HealthBar: React.FC<{ label: string, status: string, percentage: number, color: 'cyan' | 'purple' | 'pink' }> = ({ label, status, percentage, color }) => {
  const colorMap = {
    cyan: '#06b6d4',
    purple: '#a855f7',
    pink: '#ec4899'
  };
  
  const selectedColor = colorMap[color];

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-end">
        <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest">{label}</span>
        <span className="text-xs font-bold" style={{ color: selectedColor }}>{status}</span>
      </div>
      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden border border-zinc-200 relative">
        <div 
          className="h-full transition-all duration-1000" 
          style={{ 
            width: `${percentage}%`,
            backgroundColor: selectedColor
          }} 
        />
      </div>
    </div>
  );
};

export default AdminPanel;
